import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { isInWebView } from '../../../shared/utils/saveBlobDownload';

export interface AppWindowHandle {
  /** Point the window at `url` (call once the URL is known). */
  navigate: (url: string) => void;
  close: () => void;
}

/**
 * "Open in a new window" that also works inside the native app. A desktop or
 * mobile browser gets a real popup window centred over this one; the app's
 * WebView can't open windows, so there the page shows in a full-screen
 * in-page panel with a Close button instead.
 *
 * `open()` must run inside the click handler (popup blockers), before any
 * await; call `navigate()` on the returned handle once the URL is ready.
 */
export function useAppWindow() {
  const [panel, setPanel] = useState<{ title: string; url: string | null } | null>(null);

  const open = useCallback((title: string, name: string, url?: string): AppWindowHandle => {
    if (isInWebView()) {
      setPanel({ title, url: url ?? null });
      return {
        navigate: (next) => setPanel({ title, url: next }),
        close: () => setPanel(null),
      };
    }
    const width = Math.min(1280, window.screen.availWidth - 80);
    const height = Math.min(820, window.screen.availHeight - 80);
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
    const popup = window.open(url ?? '', name, `popup=yes,width=${width},height=${height},left=${left},top=${top}`);
    popup?.focus();
    return {
      navigate: (next) => {
        if (popup) {
          popup.location.href = next;
          popup.focus();
        } else {
          window.location.href = next;
        }
      },
      close: () => popup?.close(),
    };
  }, []);

  const element = (
    <Dialog fullScreen open={!!panel} onClose={() => setPanel(null)}
      PaperProps={{ sx: { bgcolor: 'var(--color-bg, #0f1115)', display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, flexShrink: 0,
        pt: 'max(8px, env(safe-area-inset-top))',
        borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)',
      }}>
        <Typography noWrap sx={{ flex: 1, fontWeight: 700, color: 'var(--color-text-strong)' }}>{panel?.title}</Typography>
        <IconButton onClick={() => setPanel(null)} aria-label="Close" sx={{ color: 'var(--color-text-strong)' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      {panel?.url
        ? <Box component="iframe" src={panel.url} title={panel.title}
            sx={{ flex: 1, width: '100%', border: 0, display: 'block' }} />
        : <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>}
    </Dialog>
  );

  return { open, element };
}
